// MA-655 — `ten_frame_sub` concept visual for FRAME-AUTHORED subtraction.
//
// Renders a single 10-cell ten frame with `a` cells filled and the LAST `b`
// of those filled cells crossed out — exactly the picture ScaffoldT10
// (src/components/scaffolds/index.tsx) already draws in its sub mode, but
// as a REGISTERED main renderer (not hint-only) so a frame-authored item
// ("Look at the ten frame. How many are left?") actually shows a frame
// instead of the generic take_away dot grid (see MA-651 §C2 — 21 items
// authored this way, previously flattened before this fix).
//
// MA-647 operand caption above (`a − b`, reused verbatim from
// StagedMathRenderers.tsx — no duplicated card styling). NO numeral other
// than the caption's own operands; the frame itself never prints a/b/total.
//
// ANSWER-SAFETY: this is NOT answer-safe by construction (same class as
// TakeAwayRenderer, not the answer-visible-by-design two_ten_frames_teen) —
// the UNCROSSED cell count IS the answer, exactly like take_away's uncrossed
// dots. The count-free aria-label ("Ten frame with some counters taken
// away.") mirrors TenFrame's own MA-116 RED-5 precedent for the same
// reason: naming the drawn count would read the answer aloud to an AT user.
//
// NOT staged (see mathVisualStage.ts STAGED_KINDS): the frame is the
// authored structure, the same argument MA-625 v1.1 §2 made for exempting
// plain `ten_frame` — this renders concrete at every mastery band.
//
// ADDITIVE — registers itself under "ten_frame_sub" (see rendererRegistry
// .tsx). The mapper (contentToEngineItem.ts, MA-655) emits this kind only
// when op==="-", a−b===total, b>0, the authored visualSupport/
// sourceStimulus_v2.type is a frame kind (excluding two_ten_frames, which
// has its own sibling kind), and a<=10 (a ten frame has exactly 10 cells).
import type { MathRendererProps } from "./rendererRegistry";
import { OperandCaption, operandCaptionSize } from "./StagedMathRenderers";

const DOT_INTACT = "var(--pal-orange, #d97742)";
const DOT_REMOVED_BG = "var(--color-bg-secondary, #f5efe3)";
const DOT_REMOVED_STROKE = "var(--color-border-secondary, #cdbfae)";
const X_COLOR = "var(--color-error, #c0392b)";

const CELL = 68;
const PITCH = 70; // CELL + 2px gap
const COLS = 5;
const PAD = 6;
const R = 22; // circle radius, matches TenFrame's own dots
const CAPTION_GAP = 14; // MA-647 — matches TakeAwayRenderer's own gap

function TenFrameSubRenderer({ a, b }: MathRendererProps) {
  const safeA = Math.max(0, Math.min(10, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 0)));
  const safeB = Math.max(0, Math.min(safeA, Math.floor(Number.isFinite(b ?? NaN) ? (b as number) : 0)));
  const firstIntact = safeA - safeB;

  const gridW = COLS * PITCH + 2 * PAD; // 362
  const gridH = 2 * PITCH + 2 * PAD; // 152
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => PAD + Math.floor(i / COLS) * PITCH;

  const cap = operandCaptionSize();
  const svgW = Math.max(gridW, cap.width);
  const svgH = cap.height + CAPTION_GAP + gridH;
  const gridOffsetX = (svgW - gridW) / 2;
  const capOffsetX = (svgW - cap.width) / 2;
  const gridOffsetY = cap.height + CAPTION_GAP;

  const xArm = (R * 0.6).toFixed(1);

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      width="100%"
      style={{ maxWidth: Math.min(360, svgW) }}
      role="img"
      aria-label="Ten frame with some counters taken away."
    >
      <OperandCaption a={safeA} b={safeB} op="-" x={capOffsetX} y={0} />
      <g transform={`translate(${gridOffsetX}, ${gridOffsetY})`}>
        {Array.from({ length: 10 }).map((_, i) => {
          const x = cellX(i);
          const y = cellY(i);
          const filled = i < safeA;
          const crossed = filled && i >= firstIntact;
          return (
            <g key={i}>
              <rect x={x} y={y} width={CELL} height={CELL} rx="8" fill="none"
                stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
              {filled && !crossed && (
                <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R} fill={DOT_INTACT} />
              )}
              {crossed && (
                <>
                  <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R}
                    fill={DOT_REMOVED_BG} stroke={DOT_REMOVED_STROKE} strokeWidth="1.5" />
                  <line x1={x + CELL / 2 - +xArm} y1={y + CELL / 2 - +xArm}
                    x2={x + CELL / 2 + +xArm} y2={y + CELL / 2 + +xArm}
                    stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
                  <line x1={x + CELL / 2 + +xArm} y1={y + CELL / 2 - +xArm}
                    x2={x + CELL / 2 - +xArm} y2={y + CELL / 2 + +xArm}
                    stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
                </>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default TenFrameSubRenderer;
